import React, { useEffect } from "react";
import DownloadLinkBox from "../Common/DownloadLinkBox";
import ResponseLoader from "../Loader/ResponseLoader";
import UserAuthStore from "../../store/userStore";
import mediaUrlAdapter from "../../api/Media/mediaUrlAdapter";
import useResolvedMediaUrlStore from "../../store/useResolvedMediaUrlStore";
import { useTheme } from "../../context/ThemeProvider";
import logger from "../../services/Logger";

const StorageFileLink = ({ href, children }) => {
  const { theme } = useTheme();
  const setLoadedUrl = useResolvedMediaUrlStore(
    (state) => state.setLoadedUrl
  );
  const loadedUrl = useResolvedMediaUrlStore((state) => state.urls[href]);
  useEffect(() => {
    const getUrl = async () => {
      try {
        const url = await mediaUrlAdapter.getUrl(href, UserAuthStore.getState().orgId);
        setLoadedUrl(href, url);
      } catch (err) {
        // Previously unhandled — the "ERROR" sentinel branch below existed but
        // was unreachable, so a failed resolution just left this stuck on
        // ResponseLoader forever with an unhandled promise rejection.
        logger.error("StorageFileLink", "Failed to resolve file URL", { err: err?.message, href });
        setLoadedUrl(href, "ERROR");
      }
    };
    if (!loadedUrl) {
      getUrl();
    }
  }, [href, loadedUrl, setLoadedUrl]);

  if (loadedUrl) {
    if (loadedUrl === "ERROR") {
      return (
        <span
          className="text-ink-red-600 dark:text-ink-red-500"
        >
          Error in generating file.
        </span>
      );
    }

    // Extract filename from href or children
    let filename = React.Children.toArray(children).join("") || "download";

    // If children text doesn't have extension, try to get from href
    if (!filename.includes(".") && href) {
      const hrefFilename = href.split("/").pop();
      if (hrefFilename && hrefFilename.includes(".")) {
        filename = hrefFilename;
      }
    }

    // If still no extension, guess from context
    const childrenText = React.Children.toArray(children).join("").toLowerCase();
    if (!filename.includes(".")) {
      if (childrenText.includes("notebook") || childrenText.includes("ipynb")) {
        filename = filename + ".ipynb";
      } else if (childrenText.includes("csv")) {
        filename = filename + ".csv";
      } else if (childrenText.includes("pdf")) {
        filename = filename + ".pdf";
      } else if (childrenText.includes("xlsx") || childrenText.includes("excel")) {
        filename = filename + ".xlsx";
      }
    }

    const fileExtension = filename.split(".").pop()?.toLowerCase() || "file";

    // Use DownloadLinkBox for a consistent download experience
    return (
      <DownloadLinkBox
        href={loadedUrl}
        filename={filename}
        fileType={fileExtension}
      />
    );
  }

  return <ResponseLoader className={"my-2"} theme={theme} />;
};

export default StorageFileLink;
